import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useListFaculty } from "../hooks/useListFaculty";
import { FacultyListItem } from "../types";
import TeachesDialog from "./TeachesDialog";

const subjectCount = (faculty: FacultyListItem) =>
  faculty.teaches.reduce(
    (total, grade) => total + grade.sections.reduce((sum, section) => sum + section.subjects.length, 0),
    0
  );

/** Faculty List: every faculty with what they teach in the active year ("View" opens the tree). */
export default function FacultyList() {
  const navigate = useNavigate();
  const { faculties, loading, error } = useListFaculty();
  const [viewing, setViewing] = useState<FacultyListItem | null>(null);

  const academicYear = faculties.find((faculty) => faculty.academicYear)?.academicYear ?? null;

  const edit = (facultyId: number) => navigate(`/app/staff/faculty/${facultyId}`);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer className="mui-table-container-style">
          <Table size="small" className="mui-table-bordered">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Login Id</TableCell>
                <TableCell>Teaches{academicYear ? ` (${academicYear})` : ""}</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {faculties.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No faculty yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                faculties.map((faculty, index) => {
                  const count = subjectCount(faculty);
                  return (
                    <TableRow key={faculty.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{faculty.name}</TableCell>
                      <TableCell>{faculty.phone}</TableCell>
                      <TableCell>{faculty.loginId}</TableCell>
                      <TableCell>
                        {count === 0 ? (
                          "—"
                        ) : (
                          <Link component="button" variant="body2" onClick={() => setViewing(faculty)}>
                            View ({count})
                          </Link>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => edit(faculty.id)}>
                            <EditIcon fontSize="small" sx={{ color: "green" }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TeachesDialog faculty={viewing} onClose={() => setViewing(null)} onEdit={edit} />
    </Box>
  );
}
